import { ContentState } from "draft-js";
import { List, Map } from "immutable";
import { SimpleMappedStore } from "@core/store/mappedStore";
import { SimpleMappedStorePresentation } from "@core/store/mappedStorePresentation";
import { SimpleStore } from "@core/store/store";
import { StorePresentation } from "@core/store/storePresentation";
import { Page } from "@utils/page";
import { CurrentProfileService } from "@domain/currentProfile/currentProfileService";
import { Post } from "@domain/post/post";
import { Course, LightCourse } from "./course";
import { CourseApi } from "./courseApi";
import { CourseDraft, CourseStatus } from "./courseDraft";
import { Exercise } from "@domain/lms/course/exercise/exercise";
import { Feedback, FeedbackInput } from "@domain/lms/course/feedback/feedback";

export class CourseService {
	courseStore = new SimpleStore<Course>((id) => this.courseApi.get(id));
	slugCourseStore = new SimpleStore<Course>((slug) => this.courseApi.getBySlug(slug));
	detailedCourseStore = new SimpleStore((id) => this.courseApi.getDetailed(id));
	favoriteCoursesPresentation = new StorePresentation(this.courseStore, (page) => this.courseApi.getFavorites(page));
	feedbackStore = new SimpleMappedStore((id, page) => this.courseApi.listFeedbacks(id, page));
	myCoursesStore = new SimpleMappedStorePresentation(this.courseStore, (status: CourseStatus | "ALL", page) =>
		this.courseApi.getMyCourses(status, page)
	);
	profileCoursesStore = new SimpleMappedStorePresentation(this.courseStore, (profileId, page) =>
		this.courseApi.getProfileCourses(profileId, page)
	);
	organizationCoursesStore = new SimpleMappedStorePresentation(this.courseStore, (organizationId, page) =>
		this.courseApi.getOrganizationCourses(organizationId, page)
	);
	workspaceCoursesStore = new SimpleMappedStorePresentation(this.courseStore, (workspaceId, page) =>
		this.courseApi.getWorkspaceCourses(workspaceId, page)
	);

	constructor(
		private courseApi: CourseApi,
		private currentProfileService: CurrentProfileService,
		initialCourses?: Map<string, Course>
	) {
		if (initialCourses) {
			this.courseStore.merge(List(initialCourses.values()));
		}
		this.slugCourseStore.bind(this.courseStore);
	}

	async toggleFavorite(course: Course): Promise<void> {
		if (!course.isFavorite) {
			await this.courseApi.setFavorite(course.id);
		} else {
			await this.courseApi.removeFavorite(course.id);
		}
		this.courseStore.update(course.id, (c) => ({ ...c, isFavorite: !course.isFavorite }));
	}

	async sendFeedback(courseId: string, feedback: FeedbackInput): Promise<void> {
		await this.courseApi.sendFeedback(courseId, feedback);
		await this.feedbackStore.fetch(courseId);
		await this.courseStore.fetch(courseId);
	}

	async getMyFeedback(courseId: string): Promise<Feedback | null> {
		try {
			return await this.courseApi.getMyFeedback(courseId);
		} catch (e) {
			if ((e as { code?: number }).code === 404) {
				return null;
			}
			throw e;
		}
	}

	async saveCourse(draft: CourseDraft, id?: string, publisherId?: string): Promise<LightCourse> {
		const course = await (id
			? this.courseApi.saveCourse(draft, id, publisherId)
			: this.courseApi.createCourse(draft, publisherId));
		return course;
	}

	async shareCourse(courseId: string, message?: ContentState, groupId?: string): Promise<Post> {
		return this.courseApi.share(courseId, message, groupId);
	}

	async submitExercises(courseId: string, chapterIndex: number, answers: List<number>): Promise<Exercise[]> {
		return this.courseApi.validate(courseId, chapterIndex, answers);
	}

	async deleteFeedback(courseId: string): Promise<void> {
		await this.courseApi.deleteFeedback(courseId);
		await this.feedbackStore.fetch(courseId);
		await this.courseStore.fetch(courseId);
	}

	async deleteCourse(courseId: string): Promise<void> {
		await this.courseApi.delete(courseId);
		this.courseStore.remove(courseId);
	}

	suggest = (count: number): Promise<Page<Course>> => {
		return this.courseApi.getSuggestions(
			{ page: 0, size: count },
			this.currentProfileService.currentProfile.get()?.categories ?? []
		);
	};

	search = this.courseApi.search.bind(this.courseApi);

	view(courseId: string): Promise<void> {
		return this.courseApi.view(courseId);
	}
}
